import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../lib/api";
import { useI18n } from "../i18n";
import { Store, MapPin, Globe, Instagram, Facebook } from "lucide-react";
import ProductCard from "../components/store/ProductCard";

const SellerStore = () => {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [seller, setSeller] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiClient.get(`/sellers/${slug}`).then((r) => alive && setSeller(r.data)).catch(() => alive && setSeller(null)),
      apiClient.get(`/sellers/${slug}/products`).then((r) => alive && setItems(r.data.items || [])).catch(() => alive && setItems([])),
    ]).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-ink-muted">Loading…</div>;
  if (!seller) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <h1 className="font-display text-4xl tracking-tighter">{lang === "nl" ? "Verkoper niet gevonden" : "Seller not found"}</h1>
        <Link to="/shop" className="btn-primary mt-6">{lang === "nl" ? "Naar winkel" : "Go to shop"}</Link>
      </div>
    );
  }

  const bio = (lang === "nl" ? seller.bio_nl : seller.bio_en) || "";

  return (
    <div data-testid="seller-store-page" className="bg-bone min-h-screen">
      {/* Banner */}
      <div className="relative h-48 md:h-64 bg-secondary-warm overflow-hidden">
        {seller.banner_url && <img src={seller.banner_url} alt="" className="w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-bone to-transparent" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 -mt-16 relative z-10">
        <div className="border border-black/10 bg-white p-8 flex flex-col md:flex-row gap-6 items-start">
          <div className="w-20 h-20 bg-secondary-warm rounded-full flex items-center justify-center overflow-hidden shrink-0">
            {seller.logo_url ? <img src={seller.logo_url} alt="" className="w-full h-full object-cover" /> : <Store className="w-8 h-8 text-terracotta" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-label text-terracotta">{lang === "nl" ? "VERKOPER OP SNAPSHOP" : "SELLER ON SNAPSHOP"}</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light" data-testid="seller-name">{seller.company_name}</h1>
            {bio && <p className="text-ink-muted mt-3 max-w-2xl leading-relaxed">{bio}</p>}
            <div className="flex flex-wrap gap-4 mt-5 text-xs font-label text-ink-muted">
              {(seller.city || seller.country) && (
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {[seller.city, seller.country].filter(Boolean).join(", ")}</span>
              )}
              {seller.website_url && (
                <a href={seller.website_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-ink"><Globe className="w-3.5 h-3.5" /> Website</a>
              )}
              {seller.instagram_url && (
                <a href={seller.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-ink"><Instagram className="w-3.5 h-3.5" /> Instagram</a>
              )}
              {seller.facebook_url && (
                <a href={seller.facebook_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-ink"><Facebook className="w-3.5 h-3.5" /> Facebook</a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-display text-2xl mb-6">{lang === "nl" ? "Producten" : "Products"}{items.length > 0 && <span className="text-ink-muted text-base ml-3">{items.length}</span>}</h2>
          {items.length === 0 ? (
            <div className="text-ink-muted text-sm">{lang === "nl" ? "Nog geen producten." : "No products yet."}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 pb-16">
              {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerStore;
